<template>
  <b-modal
    v-model="isOpen"
    has-modal-card
    :can-cancel="['escape', 'x', 'outside']"
    trap-focus
  >
    <div class="modal-card request-user-card">
      <header class="modal-card-head">
        <h2 class="modal-card-title">Request a new user</h2>
        <button class="delete" @click="close"></button>
      </header>
      <section class="modal-card-body request-user">
        <p>
          Someone on your company's email domain who isn't on Teamwrkr yet? Ask your company's tenant owner or an
          admin to add them. Once they approve, the person joins your company and your team, and verifies their
          email when they first sign in. People from outside your company's domain are added by an admin directly.
        </p>
        <form @submit.prevent="submit">
          <b-field label="First name">
            <b-input v-model="firstName" maxlength="100" :has-counter="false" :disabled="loading" required />
          </b-field>
          <b-field label="Last name">
            <b-input v-model="lastName" maxlength="100" :has-counter="false" :disabled="loading" required />
          </b-field>
          <b-field label="Work email">
            <b-input v-model="email" type="email" :disabled="loading" required />
          </b-field>
          <div class="request-user__actions">
            <b-button
              type="is-primary"
              native-type="submit"
              :loading="loading"
              :disabled="!canSubmit || loading"
            >
              Send request
            </b-button>
          </div>
        </form>
        <p v-if="errorMessage" class="request-user__state request-user__state--error">{{ errorMessage }}</p>
        <p v-if="successMessage" class="request-user__state request-user__state--success">{{ successMessage }}</p>
      </section>
    </div>
  </b-modal>
</template>

<script>
// spec-admin-gated-company-access: a manager (or admin) asks for a NEW person to be added to the
// company and their team (POST /users/access-requests). The owner / an admin approves; nobody is
// given admin or financial access by this.
export default {
  name: 'RequestNewUserModal',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    config: {
      type: Object,
      required: true,
    },
  },
  emits: ['update:modelValue', 'requested'],
  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      loading: false,
      errorMessage: '',
      successMessage: '',
    }
  },
  computed: {
    isOpen: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      },
    },
    canSubmit() {
      return Boolean(this.firstName.trim() && this.lastName.trim() && this.email.trim())
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.reset()
    },
  },
  methods: {
    reset() {
      this.firstName = ''
      this.lastName = ''
      this.email = ''
      this.errorMessage = ''
      this.successMessage = ''
    },
    close() {
      this.isOpen = false
    },
    errorFrom(error) {
      const data = error?.response?.data || {}
      if (data.code === 'already_in_company') return 'This person is already in your company. Invite them to your team instead.'
      if (data.code === 'other_company') return 'This person belongs to (or has asked to join) another company.'
      if (data.code === 'outside_domain') return "Only addresses on your company's own email domain can be requested. Ask an admin to add someone from outside."
      if (data.code === 'existing_account') return 'This email already has a Teamwrkr account. Ask them to request access themselves.'
      if (data.code === 'already_requested') return data.message || 'Another manager has already asked for this person.'
      if (data.code === 'too_many_requests') return data.message || 'You have too many open requests for new users. Wait for an admin to decide some first.'
      if (data.code === 'not_a_manager') return 'Only managers and company admins can request new users.'
      if (error?.response?.status === 429) return 'Too many requests. Please try again later.'
      return data.message || 'Unable to send the request. Please try again.'
    },
    async submit() {
      if (!this.canSubmit || this.loading) return
      this.loading = true
      this.errorMessage = ''
      this.successMessage = ''
      const name = `${this.firstName.trim()} ${this.lastName.trim()}`
      try {
        const response = await this.axios.post(
          `${process.env.VUE_APP_API_URL}/users/access-requests`,
          { firstName: this.firstName.trim(), lastName: this.lastName.trim(), email: this.email.trim() },
          this.config,
        )
        if (response.data?.access === 'needs_teamwrkr') {
          this.errorMessage = 'Your company has no admin who can approve new people yet. Contact Teamwrkr.'
          return
        }
        this.successMessage = `Request sent. Your company admin has been asked to approve ${name}.`
        this.firstName = ''
        this.lastName = ''
        this.email = ''
        this.$emit('requested', response.data)
      } catch (error) {
        this.errorMessage = this.errorFrom(error)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.request-user-card {
  max-width: 520px;
  width: 100%;
}

.request-user {
  padding: $space-5;
  text-align: left;

  p {
    margin-bottom: $space-4;
    color: $color-text-muted;
  }
}

.request-user__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: $space-3;
}

.request-user__state {
  margin-top: $space-3;

  &--error {
    color: $color-danger;
  }

  &--success {
    color: $color-success-strong;
  }
}
</style>
